import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Auth from './pages/Auth';
import SchoolDetail from './pages/SchoolDetail';
import BuildMyList from './pages/BuildMyList';
import SchoolMatcher from './pages/SchoolMatcher';
import PersonalStatement from './pages/PersonalStatement';
import About from './pages/About';
import Landing from './pages/Landing';
import Onboarding from './pages/Onboarding';
import Guide from './pages/Guide';
import Stats from './pages/Stats';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import { supabase } from './lib/supabase';
import ProtectedRoute from './components/ProtectedRoute';
import './styles/globals.css';

function AppShell({ session, children }) {
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f5f0e8' }}>
      <Sidebar session={session} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}

function ComingSoon({ name }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, padding: '4rem', background: '#f5f0e8' }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: '#eee8dc', border: '0.5px solid #ddd6c6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="#065f46" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      </div>
      <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.4rem', fontWeight: 700, color: '#0a0a0a' }}>{name}</div>
      <div style={{ fontSize: '0.875rem', color: '#8a8070' }}>Coming soon -- check back after Phase 2</div>
    </div>
  );
}

function AuthRoute({ session, needsOnboarding, children }) {
  if (!session) return <Navigate to="/login" replace />;
  if (needsOnboarding) return <Navigate to="/onboarding" replace />;
  return children;
}

export default function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        supabase.from('profiles').select('onboarding_complete, subscription_active').eq('id', session.user.id).single()
          .then(({ data }) => {
            setNeedsOnboarding(!data || !data.onboarding_complete);
            setSubscriptionActive(data?.subscription_active === true);
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        supabase.from('profiles').select('onboarding_complete, subscription_active').eq('id', session.user.id).single()
          .then(({ data }) => {
            setNeedsOnboarding(!data || !data.onboarding_complete);
            setSubscriptionActive(data?.subscription_active === true);
          });
      } else {
        setNeedsOnboarding(false);
        setSubscriptionActive(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f0e8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 28, height: 28, border: '3px solid #ddd6c6', borderTopColor: '#065f46', borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/onboarding" element={session ? <Onboarding session={session} onComplete={() => setNeedsOnboarding(false)} /> : <Navigate to="/login" replace />} />
        <Route path="/about" element={<About />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/stats" element={<Stats />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/" element={session ? <Navigate to="/dashboard" replace /> : <Landing />} />
        <Route path="/login"  element={session ? <Navigate to="/dashboard" replace /> : <Auth />} />
        <Route path="/signup" element={session ? <Navigate to="/dashboard" replace /> : <Auth />} />
        <Route path="/*" element={
          <AuthRoute session={session} needsOnboarding={needsOnboarding}>
            <AppShell session={session}>
              <Routes>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard session={session} />} />
                <Route path="matcher" element={
                  <ProtectedRoute subscriptionActive={subscriptionActive}>
                    <SchoolMatcher session={session} />
                  </ProtectedRoute>
                } />
                <Route path="school/:id" element={
                  <ProtectedRoute subscriptionActive={subscriptionActive}>
                    <SchoolDetail session={session} />
                  </ProtectedRoute>
                } />
                <Route path="schools" element={
                  <ProtectedRoute subscriptionActive={subscriptionActive}>
                    <BuildMyList session={session} />
                  </ProtectedRoute>
                } />
                <Route path="personal-statement" element={
                  <ProtectedRoute subscriptionActive={subscriptionActive}>
                    <PersonalStatement session={session} />
                  </ProtectedRoute>
                } />
                <Route path="checklist" element={<ComingSoon name="Checklists" />} />
                <Route path="essays"    element={<ComingSoon name="Essay Prompts" />} />
                <Route path="deadlines" element={<ComingSoon name="Deadlines" />} />
                <Route path="settings"  element={<ComingSoon name="Settings" />} />
              </Routes>
            </AppShell>
          </AuthRoute>
        } />
      </Routes>
    </Router>
  );
}
